import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Button, Badge, Table } from "react-bootstrap";
import { isMarketplaceOrder } from "../../../../utils/marketplace";

import {
  getPriceAndLabelForSuperProductStatus,
  showPopinProductCredit,
  showPopinProductRefund,
  showPopinMultipleProductCreditOrRefund,
  showPopinCreditInvoiceItem,
  showPopinRefundInvoiceItem,
  showPopinRefundSalad,
  showPopinCreditSalad,
  restartSaladPrep,
  SUPPORT_MODAL_SHOW_MULTIPLE_CREDIT,
  SUPPORT_MODAL_SHOW_MULTIPLE_REFUND,
} from "../../actions";
import OrderProductOptionBadge from "../OrderProductOptionBadge";
import { rd } from "../../../../utils";
import "../../css/OrderProducts.css";
import moment from "moment/moment";
import Salads from "./Salads";
import {
  orderProductRemainingActionableQty,
  orderRemainingRecreditAmount,
  orderRemainingRefundableAmount,
  orderIsRefundable,
} from "../../utils";
import OrderProductPackingStatus from "./OrderProductPackingStatus";
import OrderProductVerifStatus from "./OrderProductVerifStatus";
import OrderProductChangeStatus from "./OrderProductChangeStatus";


const InternalOrderProducts = ({
  order,
  refundDisabled,
  showPopinCredit,
  showPopinRefund,
  showPopinMultipleCredit,
  showPopinMultipleRefund,
  showPopinCreditInvoiceItem,
  showPopinRefundInvoiceItem,
  showPopinRefundSalad,
  showPopinCreditSalad,
  restartSaladPrep,
  onRestartSaladPrep,
  readOnly,
  user,
}) => {
  moment.locale("fr");
  let orderProducts = order.products;
  const dynamicProducts = order.dynamic_products || [];

  // Arrondi à 2 décimales pour aligner comparaisons et affichages : sinon
  // un cap brut 0.19999999999998863 (affiché "0,20€") refuserait une saisie
  // de 0,20€.
  const orderRefundableLeft = rd(orderRemainingRefundableAmount(order));
  const orderRecreditableLeft = rd(orderRemainingRecreditAmount(order));

  // Passe unique sur les produits : on calcule prix unitaire + éligibilité
  // pour éviter d'appeler getPriceAndLabelForSuperProductStatus plusieurs
  // fois par produit (une fois pour hasAny*, une fois dans la boucle JSX).
  const productEligibility = orderProducts.map((op) => {
    const { pricePerUnitQuantity } = getPriceAndLabelForSuperProductStatus(op);
    const roundedUnit = pricePerUnitQuantity > 0 ? rd(pricePerUnitQuantity) : 0;
    return {
      remainingActionableQty: orderProductRemainingActionableQty(op),
      roundedUnit,
      unitPriceOverCredit: roundedUnit > 0 && roundedUnit > orderRecreditableLeft,
      unitPriceOverRefund: roundedUnit > 0 && roundedUnit > orderRefundableLeft,
    };
  });

  const hasAnyActionableProduct = productEligibility.some(
    (e) => e.remainingActionableQty > 0,
  );
  // Un produit n'est éligible au bulk que s'il a encore des unités
  // actionnables ET un prix unitaire qui rentre dans le cap restant —
  // sinon il serait grisé dans le modal, donc inutile de l'ouvrir.
  const hasAnyCreditableProduct = productEligibility.some(
    (e) => e.remainingActionableQty > 0 && e.roundedUnit > 0 && !e.unitPriceOverCredit,
  );
  const hasAnyRefundableProduct = productEligibility.some(
    (e) => e.remainingActionableQty > 0 && e.roundedUnit > 0 && !e.unitPriceOverRefund,
  );

  const refundCapReachedTitle =
    orderRefundableLeft <= 0
      ? "Montant maximum déjà remboursé/recrédité"
      : !hasAnyRefundableProduct
        ? `Aucun produit n'a un prix unitaire compatible avec le montant restant à rembourser (${orderRefundableLeft}€)`
        : undefined;
  const creditCapReachedTitle =
    orderRecreditableLeft <= 0
      ? "Montant maximum déjà recrédité/remboursé"
      : !hasAnyCreditableProduct
        ? `Aucun produit n'a un prix unitaire compatible avec le montant restant à recréditer (${orderRecreditableLeft}€)`
        : undefined;

  // Raisons de blocage à exposer en title : un <button disabled> n'affiche pas
  // son title natif (pas d'événements souris), on le porte donc sur la cellule
  // englobante (<th>/<td>). Chaque libellé est undefined quand la cause ne
  // s'applique pas, l'ordre `||` reproduit la priorité des conditions disabled.
  const noAccessTitle = readOnly
    ? "Vous n'avez pas les droits nécessaires"
    : undefined;
  const updatingTitle = order.updating
    ? "Commande en cours de mise à jour"
    : undefined;
  const uberTitle = isMarketplaceOrder(order)
    ? "Action indisponible sur une commande marketplace (Uber Eats, Deliveroo)"
    : undefined;
  const noRefundableMoneyTitle = refundDisabled
    ? "Aucun montant remboursable (payé sans moyen remboursable ou déjà remboursé)"
    : undefined;
  const noActionableTitle = !hasAnyActionableProduct
    ? "Tous les produits ont déjà été recrédités/remboursés"
    : undefined;
  const creditBulkTitle =
    noAccessTitle || updatingTitle || uberTitle || noActionableTitle
    || creditCapReachedTitle;
  const refundBulkTitle =
    noAccessTitle || noRefundableMoneyTitle || noActionableTitle
    || refundCapReachedTitle;

  return (
    <React.Fragment>
      {orderProducts.length > 0 && (
        <Table size="sm" hover className="OrderProduct">
          <tbody>
            <tr>
              <th></th>
              <th>Produit</th>
              <th></th>
              <th>Statut</th>
              <th>Zone</th>
              <th title={creditBulkTitle}>
                <Button
                  variant="primary"
                  size="xs"
                  onClick={() => showPopinMultipleCredit(order, user)}
                  disabled={
                    readOnly ||
                    order.updating ||
                    isMarketplaceOrder(order) ||
                    !hasAnyActionableProduct ||
                    orderRecreditableLeft <= 0 ||
                    !hasAnyCreditableProduct
                  }
                  title={creditBulkTitle}
                >
                  Recrédit
                </Button>
              </th>
              <th title={refundBulkTitle}>
                <Button
                  disabled={
                    readOnly ||
                    refundDisabled ||
                    !hasAnyActionableProduct ||
                    orderRefundableLeft <= 0 ||
                    !hasAnyRefundableProduct
                  }
                  title={refundBulkTitle}
                  variant="warning"
                  size="xs"
                  onClick={() => showPopinMultipleRefund(order, user)}
                >
                  Remboursement
                </Button>
              </th>
            </tr>
            {orderProducts.map((op, opIdx) => {
              let p = op.product;
              if (!p) {
                p = {
                  id: 0,
                  selling_name: "produit inconnu",
                  selling_method: "",
                  possible_areas: [],
                  area: 0,
                  shelf: 0,
                  tier: 0,
                  logistic_title: "",
                };
              }
              let possible_areas = p.possible_areas || [];
              const selling_method =
                op.selling_method || op.product.selling_method;
              const areas_tooltip = possible_areas.reduce(
                (result, pa, current_index) => {
                  if (current_index > 0) {
                    result += ",";
                  }
                  result += " " + pa.area_string + " S:" + pa.real_stock;
                  return result;
                },
                "Zonages avec du stock :"
              );

              const {
                remainingActionableQty,
                roundedUnit,
                unitPriceOverCredit,
                unitPriceOverRefund,
              } = productEligibility[opIdx];
              const productActionedTitle = remainingActionableQty === 0
                ? "Produit déjà entièrement recrédité/remboursé"
                : undefined;
              const creditDisabledTitle =
                noAccessTitle || updatingTitle || uberTitle || productActionedTitle
                || (unitPriceOverCredit
                  ? `Prix unitaire (${roundedUnit}€) supérieur au montant restant à recréditer (${orderRecreditableLeft}€)`
                  : creditCapReachedTitle);
              const refundDisabledTitle =
                noAccessTitle || noRefundableMoneyTitle || productActionedTitle
                || (unitPriceOverRefund
                  ? `Prix unitaire (${roundedUnit}€) supérieur au montant restant à rembourser (${orderRefundableLeft}€)`
                  : refundCapReachedTitle);

              return (
                <tr key={op.id} className={op.updating ? "updating" : ""}>
                  <td>
                    <b>
                      {op.quantity}
                      {selling_method === "weight" ? "g" : null}
                    </b>
                  </td>
                  <td>
                    <a
                      href={
                        process.env.REACT_APP_PRODUCTS + `/products/${p.id}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {p.selling_name}
                      {p.logistic_title ? (
                        <strong> - {p.logistic_title}</strong>
                      ) : null}
                    </a>
                    &nbsp;
                    {op.isReplaced ? (
                      <Badge
                        variant="default"
                        style={{ backgroundColor: "#4f1680" }}
                      >
                        remplacé
                      </Badge>
                    ) : null}
                    {op.isReplace ? (
                      <Badge
                        variant="default"
                        style={{ backgroundColor: "#7222b8" }}
                      >
                        remplacement
                      </Badge>
                    ) : null}
                    {op.is_promo ? (
                      <Badge
                        variant="default"
                        style={{ backgroundColor: "#fd7a7a" }}
                      >
                        promo
                      </Badge>
                    ) : null}
                    {op.quantity_recredit ? (
                      <Badge variant="default">
                        recredit: {op.quantity_recredit}
                        {selling_method === "weight" ? "g" : null}
                      </Badge>
                    ) : null}
                    {op.quantity_refund ? (
                      <Badge variant="default">
                        remboursé: {op.quantity_refund}
                        {selling_method === "weight" ? "g" : null}
                      </Badge>
                    ) : null}
                    {op.weight && op.weight > 1 ? (
                      <Badge
                        variant="default"
                        style={{
                          backgroundColor:
                            op.weight > 10 ? "#7D00DC" : "#8989e2",
                        }}
                      >
                        {Math.round(op.weight)} k
                      </Badge>
                    ) : null}
                    {op.options.length > 0 &&
                      op.options.map((o) => (
                        <OrderProductOptionBadge option={o} key={o.id} />
                      ))}
                    <OrderProductPackingStatus status={op.packing_status} />
                    <OrderProductVerifStatus order={order} order_product={op} />
                  </td>
                  <td>
                    {op.is_expensive ? (
                      <span style={{ color: "red" }} title={"Produit cher"}>
                        $$
                      </span>
                    ) : null}
                  </td>
                  <td>
                    <OrderProductChangeStatus order={order} order_product={op}/>
                  </td>
                  <td title={areas_tooltip}>
                    {p.area}.{p.shelf}.{p.tier}
                  </td>
                  <td title={creditDisabledTitle}>
                    <Button
                      variant="primary"
                      size="xs"
                      onClick={() => showPopinCredit(op, user, order)}
                      disabled={
                        readOnly ||
                        order.updating ||
                        isMarketplaceOrder(order) ||
                        remainingActionableQty === 0 ||
                        orderRecreditableLeft <= 0 ||
                        unitPriceOverCredit
                      }
                      title={creditDisabledTitle}
                    >
                      Recrédit
                    </Button>
                  </td>
                  <td title={refundDisabledTitle}>
                    <Button
                      disabled={
                        readOnly ||
                        refundDisabled ||
                        remainingActionableQty === 0 ||
                        orderRefundableLeft <= 0 ||
                        unitPriceOverRefund
                      }
                      title={refundDisabledTitle}
                      variant="warning"
                      size="xs"
                      onClick={() => showPopinRefund(op, order)}
                    >
                      Remboursement
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {dynamicProducts.length > 0 && (
        <Salads
          order={order}
          user={user}
          showPopinCredit={showPopinCredit}
          showPopinRefund={showPopinRefund}
          showPopinCreditInvoiceItem={showPopinCreditInvoiceItem}
          showPopinRefundInvoiceItem={showPopinRefundInvoiceItem}
          showPopinRefundSalad={showPopinRefundSalad}
          showPopinCreditSalad={showPopinCreditSalad}
          onRestartSaladPrep={onRestartSaladPrep}
          readOnly={readOnly}
          refundDisabled={refundDisabled}
        />
      )}
    </React.Fragment>
  );
};

InternalOrderProducts.propTypes = {
  order: PropTypes.object,
  user: PropTypes.object,
  readOnly: PropTypes.bool,
  refundDisabled: PropTypes.bool,
  showPopinCredit: PropTypes.func,
  showPopinRefund: PropTypes.func,
  showPopinMultipleCredit: PropTypes.func,
  showPopinMultipleRefund: PropTypes.func,
  showPopinCreditInvoiceItem: PropTypes.func,
  showPopinRefundInvoiceItem: PropTypes.func,
  showPopinRefundSalad: PropTypes.func,
  showPopinCreditSalad: PropTypes.func,
};

export const OrderProducts = connect(
  (state, ownProp) => {
    const replaceOrderProducts = ownProp.order.products.filter(
      (e) => e.replacing_data.replace
    );
    let replaceOrderProductIds = replaceOrderProducts.map((e) => e.id);
    let replacedOrderProductIds = replaceOrderProducts.map(
      (e) => e.replacing_data.replace
    );

    for (let op of ownProp.order.products) {
      op.isReplace = replaceOrderProductIds.includes(op.id);
      op.isReplaced = replacedOrderProductIds.includes(op.id);
    }

    return {
      orderProducts: ownProp.order.products.sort(function (a, b) {
        const diffArea = a.product.area - b.product.area;
        const diffShelf = a.product.shelf - b.product.shelf;
        const diffTier = a.product.tier - b.product.tier;
        for (let diff of [diffArea, diffShelf, diffTier]) {
          if (diff !== 0) return diff;
        }
        return 0;
      }),
      // La commande est remboursable s'il reste de l'argent réel sur un moyen
      // de paiement remboursable (cf. order.refundable_amount).
      refundDisabled: !orderIsRefundable(ownProp.order),
    };
  },
  (dispatch) => {
    return {
      showPopinCredit: (op, user, order) => {
        dispatch(showPopinProductCredit(op, user, order));
      },
      showPopinRefund: (op, order) => {
        dispatch(showPopinProductRefund(op, order));
      },
      showPopinMultipleCredit: (order, user) => {
        dispatch(
          showPopinMultipleProductCreditOrRefund(
            order,
            user,
            SUPPORT_MODAL_SHOW_MULTIPLE_CREDIT
          )
        );
      },
      showPopinMultipleRefund: (order, user) => {
        dispatch(
          showPopinMultipleProductCreditOrRefund(
            order,
            user,
            SUPPORT_MODAL_SHOW_MULTIPLE_REFUND
          )
        );
      },
      showPopinCreditInvoiceItem: (invoiceItem, order) => {
        dispatch(showPopinCreditInvoiceItem(invoiceItem, order));
      },
      showPopinRefundInvoiceItem: (invoiceItem, order) => {
        dispatch(showPopinRefundInvoiceItem(invoiceItem, order));
      },
      showPopinRefundSalad: (salad, order) => {
        dispatch(showPopinRefundSalad(salad, order));
      },
      showPopinCreditSalad: (salad, order) => {
        dispatch(showPopinCreditSalad(salad, order));
      },
      onRestartSaladPrep: (orderId, choicesetTaskId) => {
        dispatch(restartSaladPrep(orderId, choicesetTaskId));
      },
    };
  }
)(InternalOrderProducts);
